import * as React from 'react'
import { cn } from '../../lib/utils'
import type { BadgeColor } from './badge'
import { softSurface, softText, accentText } from './badge-colors'

/**
 * StatusStat — the prominent, borderless counterpart to {@link Badge}. Where a
 * Badge is a compact pill for tables and dense rows, a StatusStat displays the
 * same state large and icon-led for cards and detail headers. Two layouts:
 *
 *  - **band**: an edge-to-edge tinted strip (icon + colored text on a soft
 *    surface) — e.g. the stage footer on a deal card ("Target", "Closed Won").
 *  - **inline**: a borderless icon + text where the color rides on the icon and
 *    the text stays neutral — e.g. the "17 days since last update" freshness
 *    stat, where green = fresh and red = stale.
 *
 * Colors reuse the Badge palette (`BadgeColor`) so a status reads the same hue
 * whether it renders as a badge or a stat.
 *
 * Always pass text `children` — the icon is decorative (`aria-hidden`) and color
 * is never the sole signal, so an icon-only StatusStat would be silent to AT.
 */

export interface StatusStatProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Status color — shares the Badge palette. Defaults to `gray`. */
  color?: BadgeColor
  /** `band` (edge-to-edge tinted strip) or `inline` (borderless icon + text). */
  layout?: 'band' | 'inline'
  /** `md` (default, 20px icon / 14px text) or `sm` (16px / 12px) for dense
   *  contexts like a deal card. */
  size?: 'sm' | 'md'
  /** Leading icon — inherits the band text color or the accent in inline. */
  icon?: React.ReactNode
  /** Wrap the icon in a soft circular background (the freshness-stat treatment). */
  iconChip?: boolean
}

export const StatusStat = React.forwardRef<HTMLDivElement, StatusStatProps>(
  (
    { className, color = 'gray', layout = 'band', size = 'md', icon, iconChip, children, ...props },
    ref
  ) => {
    const isBand = layout === 'band'
    const isSm = size === 'sm'
    // The band carries a larger, prominent glyph; inline sits in a text row, so
    // its icon tracks the font size (~text height) at both sizes.
    const iconSize = isBand
      ? isSm
        ? '[&_svg]:size-4'
        : '[&_svg]:size-5'
      : isSm
        ? '[&_svg]:size-3.5'
        : '[&_svg]:size-4'
    return (
      <div
        ref={ref}
        className={cn(
          'items-center [&_svg]:shrink-0',
          iconSize,
          isSm ? 'gap-1.5 text-xs' : 'gap-2 text-sm',
          isBand
            ? cn(
                'flex w-full font-semibold',
                isSm ? 'px-3 py-2' : 'px-4 py-3',
                softSurface[color],
                softText[color]
              )
            : 'inline-flex text-fbui-gray-700',
          className
        )}
        {...props}
      >
        {icon && (
          // The icon carries the brighter accent shade in both layouts — it
          // lifts off the soft band fill and, in `inline`, keeps the color on
          // the icon while the surrounding text stays neutral. `iconChip` seats
          // it in a soft circle (the freshness treatment).
          <span
            aria-hidden="true"
            className={cn(
              'inline-flex items-center justify-center',
              accentText[color],
              iconChip &&
                cn('rounded-full', softSurface[color], isSm ? 'size-6' : 'size-7')
            )}
          >
            {icon}
          </span>
        )}
        {children}
      </div>
    )
  }
)
StatusStat.displayName = 'StatusStat'
